import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';

import { BreadcrumbItemType, ExplorerItem } from '@/types/file-explorer-types';

import { useCalculateRowsPerPage } from '@/hooks/use-calculate-rows-per-page';

import { FileExplorer } from '@/components/FileExplorer/FileExplorer';

import { useFileExplorerStore } from '@/stores/fileExplorerStore';
import { useGlobalStore } from '@/stores/globalStore';

import { ROW_HEIGHT } from '@/constants/global-constants';
// import { executorRoutes } from '@/constants/route-settings';
import { ExplorerItemType } from '@/constants/table-columns-config';

export function DirectoryPage() {
  const [isLoading, setIsLoading] = useState(true);
  const { getStateByType, getBreadcrumbItems } = useFileExplorerStore();
  const { fileUuidToMove, setNavigationSource } = useGlobalStore();
  const abortControllerRef = useRef<AbortController | null>(null);

  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();

  const [currentUUID, setCurrentUUID] = useState('');
  const [currentEntityType, setCurrentEntityType] =
    useState<ExplorerItemType>();
  const [items, setItems] = useState<ExplorerItem[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItemType[]>([]);
  const rowsPerPage = useCalculateRowsPerPage(ROW_HEIGHT);
  const [totalCount, setTotalCount] = useState(0);
  // Serialize searchParams to a stable string to avoid double-firing
  const searchParamsString = searchParams.toString();

  useEffect(() => {
    // Prefer URL query params as the source of truth
    const typeParam = searchParams.get('t');
    const uuidParam = searchParams.get('id');

    const mapParamToType = (param: string | null): ExplorerItemType => {
      switch (param) {
        case 'dir':
          return ExplorerItemType.DIRECTORY;
        case 'obj':
          return ExplorerItemType.INSURANCE_OBJECT;
        case 'area':
          return ExplorerItemType.INSURANCE_AREA;
        case 'agree':
          return ExplorerItemType.AGREEMENT;
        case 'file':
          return ExplorerItemType.FILE;
        default:
          return ExplorerItemType.AGREEMENT;
      }
    };

    if (typeParam || uuidParam !== null) {
      setCurrentEntityType(mapParamToType(typeParam));
      setCurrentUUID(uuidParam ?? '');
      return;
    }

    // Fallback to location.state for backward compatibility
    const initialState = location.state || {};
    const initialUUID = initialState.uuid ?? '';
    const initialEntityType =
      initialState.entityType ?? ExplorerItemType.AGREEMENT;

    setCurrentUUID(initialUUID);
    setCurrentEntityType(initialEntityType);

    // Initialize canonical query params if none are present
    if (!typeParam && uuidParam === null) {
      setSearchParams({ t: 'agree' });
    }
  }, [searchParamsString]);

  const isAgreementRootLevel = useMemo(() => {
    return (
      currentEntityType === ExplorerItemType.AGREEMENT && currentUUID === ''
    );
  }, [currentEntityType, currentUUID]);

  const fetchData = async (
    entityType: ExplorerItemType,
    pagination?: { pageIndex: number; pageSize: number },
  ) => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new AbortController for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setIsLoading(true);
    try {
      // Pass pagination parameters to getStateByType for paginated entity types
      const state = await getStateByType(
        entityType,
        currentUUID,
        pagination,
        isAgreementRootLevel,
      );

      // Check if the operation was aborted before continuing
      if (signal.aborted) return;

      const fetchedItems = state.rootIds.map((uuid) => state.entities[uuid]);

      if (signal.aborted) return;

      if (signal.aborted) return;

      setItems(fetchedItems);
      setTotalCount(state.totalCount);
    } catch (error) {
      // Only handle non-abort errors
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Error fetching data:', error);
      }
    } finally {
      if (!signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  const refetchData = useCallback(
    (pagination?: { pageIndex: number; pageSize: number }) => {
      if (currentEntityType === undefined) return;
      fetchData(currentEntityType, pagination);
    },
    [currentUUID, currentEntityType],
  );

  // This effect will run whenever the navigation state changes, triggering a data refetch
  useEffect(() => {
    if (
      currentEntityType === undefined ||
      currentEntityType === ExplorerItemType.AGREEMENT
    )
      return;

    // Fetch data when entity type or UUID changes (driven by URL query params)
    fetchData(currentEntityType, {
      pageIndex: 0,
      pageSize: currentEntityType === ExplorerItemType.FILE ? 50 : rowsPerPage,
    });

    // Cleanup function to abort fetch when dependencies change or component unmounts
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [currentUUID, currentEntityType, fileUuidToMove]);

  // Fetch breadcrumbs separately from data
  useEffect(() => {
    let isActive = true;

    const loadBreadcrumbs = async () => {
      if (currentEntityType === undefined) return;
      // At Agreement root, show root header and skip fetching
      if (
        currentEntityType === ExplorerItemType.AGREEMENT &&
        currentUUID === ''
      ) {
        if (!isActive) return;
        setBreadcrumbs([]);
        return;
      }

      try {
        const fetchedBreadcrumbs = await getBreadcrumbItems(
          currentEntityType,
          currentUUID,
        );
        if (!isActive) return;
        setBreadcrumbs(fetchedBreadcrumbs);
      } catch (e) {
        if (!isActive) return;
        setBreadcrumbs([]);
      }
    };

    loadBreadcrumbs();

    return () => {
      isActive = false;
    };
  }, [currentEntityType, currentUUID]);

  const handleNavigate = ({
    uuid,
    entityType,
  }: {
    uuid: string;
    entityType: ExplorerItemType;
  }) => {
    setItems([]);
    const mapTypeToParam = (type: ExplorerItemType): string => {
      switch (type) {
        case ExplorerItemType.DIRECTORY:
          return 'dir';
        case ExplorerItemType.INSURANCE_OBJECT:
          return 'obj';
        case ExplorerItemType.INSURANCE_AREA:
          return 'area';
        case ExplorerItemType.AGREEMENT:
          return 'agree';
        case ExplorerItemType.FILE:
          return 'file';
        default:
          return 'agree';
      }
    };

    // Set navigation source for back button during hierarchy navigation
    setNavigationSource('myDocuments');

    // Update URL query params as the canonical route state
    if (uuid) {
      setSearchParams({ t: mapTypeToParam(entityType), id: uuid });
    } else {
      setSearchParams({ t: mapTypeToParam(entityType) });
    }
  };

  const filteredItems = useMemo(() => {
    if (isLoading) return [];

    if (!items.length) return items;

    if (!isAgreementRootLevel) return items;

    return items;
  }, [items, breadcrumbs, currentEntityType, isLoading, isAgreementRootLevel]);

  return (
    <div className="flex h-full w-full">
      <div className="flex-grow px-3 py-5 sm:px-[50px] sm:py-[50px]">
        {currentEntityType !== undefined ? (
          <FileExplorer
            isLoading={isLoading}
            items={filteredItems}
            currentUUID={currentUUID}
            breadcrumbs={breadcrumbs}
            entityType={currentEntityType}
            setCurrentUUID={setCurrentUUID}
            setCurrentEntityType={setCurrentEntityType}
            onNavigate={handleNavigate}
            refetchData={refetchData}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
          />
        ) : null}
      </div>
    </div>
  );
}
